import { useCallback } from 'react'

import { Placeholder, useAsyncResource } from '@subaio/components'

import { useApi } from 'lib/api'

import { Table } from 'components/DefaultMdxComponents/Table'

const platforms = ['android', 'ios', 'web'] as const
type Platform = (typeof platforms)[number]

type Props = {
	platform?: Platform | 'all'
}

const platformTitles: Record<Platform, string> = {
	android: 'Android',
	ios: 'iOS',
	web: 'Web',
}

export const FintechBridgeReferences = ({ platform = 'all' }: Props) => {
	const api = useApi()

	const referencesResource = useAsyncResource(
		useCallback(
			async (abortIfCancelled) =>
				Promise.all(
					(platform === 'all' ? platforms : [platform]).map(async (currentPlatform) => {
						const versions = await api.get<Array<string>>(`/fintech-bridges/${currentPlatform}`)
						abortIfCancelled()

						return { platform: currentPlatform, versions }
					}),
				),
			[api, platform],
		),
	)

	if (referencesResource.status === 'errored') return <p>SDK versions are currently unavailable.</p>

	const references = referencesResource.value ?? []

	if (platform === 'all') {
		return (
			<Table>
				<thead>
					<tr>
						{platforms.map((currentPlatform) => (
							<th key={currentPlatform}>{platformTitles[currentPlatform]}</th>
						))}
					</tr>
				</thead>
				<tbody>
					{[0, 1].map((versionIndex) => (
						<tr key={versionIndex}>
							{platforms.map((currentPlatform) => {
								const version = references.find(
									({ platform: referencePlatform }) => referencePlatform === currentPlatform,
								)?.versions[versionIndex]

								return (
									<td key={currentPlatform}>
										<Placeholder isLoading={referencesResource.status === 'loading'}>
											{version ? (
												<>
													<a href={`/docs/fintech-bridges/${currentPlatform}/${version}/`}>{version}</a>
													{versionIndex === 0 && (
														<>
															{' '}
															<code>Latest</code>
														</>
													)}
												</>
											) : (
												'—'
											)}
										</Placeholder>
									</td>
								)
							})}
						</tr>
					))}
				</tbody>
			</Table>
		)
	}

	const versions = references[0]?.versions ?? []

	return (
		<Table>
			<thead>
				<tr>
					<th>Documentation references</th>
				</tr>
			</thead>
			<tbody>
				{[0, 1].map((versionIndex) => {
					const version = versions[versionIndex]

					return (
						<tr key={versionIndex}>
							<td>
								<Placeholder isLoading={referencesResource.status === 'loading'}>
									{version ? (
										<>
											<a href={`/docs/fintech-bridges/${platform}/${version}/`}>{version}</a>
											{versionIndex === 0 && (
												<>
													{' '}
													<code>Latest</code>
												</>
											)}
										</>
									) : (
										'—'
									)}
								</Placeholder>
							</td>
						</tr>
					)
				})}
			</tbody>
		</Table>
	)
}
